import {
  MonthlyReport,
  MonthlyReportMetrics,
  ReportSiteUse
} from '../types/domain';
import {
  getMeasurementContextLabel,
  getPointLabel,
  getSiteLabelForHistory
} from './domain-data';

/**
 * FR-16 presentation helpers.
 *
 * Every figure here already exists in the snapshot — this file only labels,
 * shapes and narrates it. The arithmetic lives in build_monthly_report()
 * (migration 010) so the cron job, the app and the PDF cannot drift apart;
 * anything computed here would exist in the app only, which is exactly what
 * the server-side requirement rules out.
 */

const MONTH_NAMES = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

/** First day of the month a `YYYY-MM-DD` / `YYYY-MM` string falls in. */
export function monthStart(value: string): string {
  return `${value.slice(0, 7)}-01`;
}

/** The patient's current month, in local time. */
export function currentMonth(now: Date = new Date()): string {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return `${local.toISOString().slice(0, 7)}-01`;
}

/** Steps `offset` months from `month` — negative goes back. */
export function shiftMonth(month: string, offset: number): string {
  const year = Number(month.slice(0, 4));
  const index = Number(month.slice(5, 7)) - 1 + offset;
  const shifted = new Date(Date.UTC(year, index, 1));
  return shifted.toISOString().slice(0, 10);
}

/** "Maret 2026". */
export function formatMonthLabel(month: string): string {
  const index = Number(month.slice(5, 7)) - 1;
  const name = MONTH_NAMES[index];
  if (!name) {
    return month;
  }
  return `${name} ${month.slice(0, 4)}`;
}

/** "17 Maret 2026" — used in the PDF, where a bare ISO date reads badly. */
export function formatLongDate(date: string): string {
  const index = Number(date.slice(5, 7)) - 1;
  const name = MONTH_NAMES[index];
  if (!name) {
    return date;
  }
  return `${Number(date.slice(8, 10))} ${name} ${date.slice(0, 4)}`;
}

/** "17/03" — the chart axis, where a full date will not fit. */
export function formatShortDate(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`;
}

/**
 * The last `count` months, newest first, going back from `from`.
 *
 * The month picker offers these rather than only the months that already have
 * a snapshot: a month with no stored report is exactly the one a patient wants
 * to generate, so hiding it would hide the button that fixes it.
 */
export function recentMonths(count: number, from: string = currentMonth()): string[] {
  return Array.from({ length: count }, (_, i) => shiftMonth(from, -i));
}

export function formatNumber(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '–';
  }
  const num = Number(value);
  return Number.isInteger(num) ? String(num) : num.toFixed(digits);
}

/** Names an injection site from either model — grid point or legacy site id. */
export function formatSiteUse(use: ReportSiteUse): string {
  if (use.regionId) {
    return getPointLabel(use.regionId, use.pointRow, use.pointCol);
  }
  if (use.siteId !== null) {
    // The band is not stored on the aggregate, and a legacy site id means the
    // row predates FR-08 anyway. '2-10' is the catalog those ids came from.
    return getSiteLabelForHistory('2-10', use.siteId);
  }
  return 'Tidak tercatat';
}

/** Rows for the per-context CategoryChart and its table view. */
export function contextChartData(
  metrics: MonthlyReportMetrics
): { context: string; average: number; count: number }[] {
  return metrics.glucose.byContext
    .filter(row => row.average !== null)
    .map(row => ({
      context: getMeasurementContextLabel(
        row.context === 'tidak-tercatat' ? null : row.context
      ),
      average: Number(row.average),
      count: row.count
    }));
}

/** Rows for the daily glucose TrendChart. */
export function glucoseChartData(
  metrics: MonthlyReportMetrics
): { date: string; average: number; minimum: number; maximum: number }[] {
  return metrics.glucose.daily.map(day => ({
    date: formatShortDate(day.date),
    average: Number(day.average ?? 0),
    minimum: Number(day.minimum ?? 0),
    maximum: Number(day.maximum ?? 0)
  }));
}

/** Rows for the daily insulin TrendChart. */
export function insulinChartData(
  metrics: MonthlyReportMetrics
): { date: string; units: number }[] {
  return metrics.insulin.daily.map(day => ({
    date: formatShortDate(day.date),
    units: Number(day.units ?? 0)
  }));
}

/** Rows for the HbA1c TrendChart — trailing history, not just this month. */
export function hba1cChartData(
  metrics: MonthlyReportMetrics
): { date: string; hba1c: number }[] {
  return metrics.clinical.hba1cTrend.map(point => ({
    date: formatShortDate(point.date),
    hba1c: Number(point.hba1c)
  }));
}

/** Share of readings that landed in target, or null when there are none. */
export function inTargetPercent(metrics: MonthlyReportMetrics): number | null {
  const { readings, zones } = metrics.glucose;
  if (!readings) {
    return null;
  }
  return Math.round((zones.normal / readings) * 100);
}

/**
 * The SoW's "ringkasan perkembangan pasien" (Bagian 8).
 *
 * Plain sentences a parent can read, in the order a clinician would look:
 * did the child record anything, what did the glucose do, was the insulin
 * taken, is rotation happening. Deliberately descriptive — it reports what the
 * numbers say and never advises a dose, which is the doctor's job.
 *
 * Kept in the app rather than in the snapshot so wording can be corrected
 * without regenerating months of stored reports.
 */
export function buildProgressSummary(metrics: MonthlyReportMetrics): string[] {
  const lines: string[] = [];
  const { compliance, glucose, insulin, sites } = metrics;

  if (compliance.daysWithAnyEntry === 0) {
    return [
      'Belum ada catatan sama sekali pada bulan ini. Mulai catat gula darah dan ' +
        'suntikan insulin setiap hari agar dokter bisa melihat perkembangannya.'
    ];
  }

  lines.push(
    `Tercatat pada ${compliance.daysWithAnyEntry} dari ${compliance.daysElapsed} hari ` +
      `(${compliance.percent}%). ${
        compliance.percent >= 80
          ? 'Kebiasaan mencatatnya sudah bagus sekali, pertahankan ya!'
          : 'Coba tambah hari pencatatannya bulan depan supaya gambarannya lebih lengkap.'
      }`
  );

  if (glucose.readings > 0) {
    const inTarget = inTargetPercent(metrics);
    lines.push(
      `${glucose.readings} kali cek gula darah, rata-rata ${formatNumber(glucose.average)} mg/dL, ` +
        `${inTarget}% berada di rentang aman.`
    );
    if (glucose.hypoCount > 0 || glucose.hyperCount > 0) {
      lines.push(
        `Tercatat ${glucose.hypoCount} kali gula darah rendah dan ${glucose.hyperCount} kali ` +
          'gula darah tinggi. Tunjukkan angka ini saat kontrol berikutnya.'
      );
    }
  } else {
    lines.push('Belum ada hasil cek gula darah yang tercatat pada bulan ini.');
  }

  if (insulin.injections > 0) {
    const planned = insulin.plannedDailyUnits;
    // The planned figure is today's regimen, so the comparison only means
    // something when a regimen is actually configured.
    const gap =
      planned > 0
        ? ` Jadwal saat ini ${formatNumber(planned)} unit per hari.`
        : '';
    lines.push(
      `${insulin.injections} kali suntik insulin, total ${formatNumber(insulin.totalUnits)} unit, ` +
        `rata-rata ${formatNumber(insulin.averageDailyUnits)} unit per hari.${gap}`
    );
  } else {
    lines.push('Belum ada suntikan insulin yang tercatat pada bulan ini.');
  }

  if (sites.injections > 0) {
    lines.push(
      `Menggunakan ${sites.distinctPoints} titik suntik berbeda. ${
        sites.distinctPoints >= 5
          ? 'Rotasi lokasinya sudah baik.'
          : 'Coba lebih sering berganti lokasi suntik agar kulit tidak menebal.'
      }`
    );
  }

  if (sites.lipodystrophyCount > 0) {
    lines.push(
      `Ada ${sites.lipodystrophyCount} catatan benjolan/penebalan kulit (lipodistrofi). ` +
        'Hindari titik tersebut dan laporkan ke dokter saat kontrol.'
    );
  }

  return lines;
}

/** Maps a snake_case monthly_reports row (FR-16). */
export function mapReportRow(row: any): MonthlyReport {
  return {
    id: row.id,
    userId: row.user_id,
    periodMonth: row.period_month,
    generatedAt: row.generated_at,
    metrics: row.metrics as MonthlyReportMetrics
  };
}
